/**
 * Journal Posts — مصدر الحقيقة الوحيد لمقالات /journal.
 *
 * كل مقال يحوي:
 *  - slug                 : URL-safe identifier (يستخدم في /journal/$slug)
 *  - category             : ritual | house | craft | gifting
 *  - minutes              : زمن قراءة تقريبيّ
 *  - publishedAt          : ISO date
 *  - coverImage           : import من src/assets
 *  - title/excerpt/body   : نسختان ar + en (Markdown)
 *  - sources              : مصادر خارجيّة موثوقة (تظهر تحت المقال + JSON-LD)
 *
 * النصوص العربيّة والإنجليزيّة محفوظة هنا (وليس i18n.content.ts) لأنّها
 * طويلة جدّاً (٨٠٠–١٢٠٠ كلمة لكل لغة) وأكثر قابليّة للصيانة كحقول ضمن
 * الكائن نفسه. الـUI labels (Read more, Sources, Continue reading، إلخ)
 * تبقى في i18n.content.ts كالعادة.
 */

import editorial1 from "@/assets/editorial-1.jpg";
import editorial2 from "@/assets/editorial-2.jpg";
import editorial3 from "@/assets/editorial-3.jpg";
import journalUnboxing from "@/assets/journal-unboxing.jpg";
import journalOud from "@/assets/journal-oud.jpg";
import journalGifting from "@/assets/journal-gifting.jpg";
import journalAtelier from "@/assets/journal-atelier.jpg";

export type JournalCategory = "ritual" | "house" | "craft" | "gifting";

export interface JournalSource {
  title: string;
  url: string;
  publisher?: string;
}

export interface JournalPost {
  slug: string;
  category: JournalCategory;
  minutes: number;
  publishedAt: string; // ISO
  coverImage: string;
  /** Bilingual content. Body is Markdown. */
  en: { title: string; excerpt: string; body: string };
  ar: { title: string; excerpt: string; body: string };
  sources: JournalSource[];
}

// ─────────────────────────────────────────────
// THE 9 POSTS
// ─────────────────────────────────────────────

export const JOURNAL_POSTS: JournalPost[] = [
  // ── Ritual / Unboxing ──────────────────────
  {
    slug: "the-ritual-of-opening",
    category: "ritual",
    minutes: 6,
    publishedAt: "2026-04-12",
    coverImage: journalUnboxing,
    en: {
      title: "The Ritual of Opening: Why a Fragrance Begins Before the First Spray",
      excerpt:
        "In luxury perfumery, the box is not packaging — it is the first chapter. Here is why the way you open a bottle shapes how you smell it.",
      body: `In luxury perfumery, the experience of a fragrance does not begin on the skin. It begins at the door — with the weight of a parcel, the resistance of a ribbon, the soft sound of tissue parting. Long before the first spray, your nervous system is already deciding whether what is inside will be precious or ordinary.

This is not romantic exaggeration. The unboxing moment is a measurable psychological event. When a package feels considered — sealed edges, structured cardboard, fabric-lined interior — the brain interprets the friction as a signal of value. Each second of slow reveal builds anticipation, and anticipation, neurologically, is closer to pleasure than the reveal itself.

## The architecture of anticipation

Luxury houses understand this intuitively. Harrods built an entire culture of "fragrance reveal" videos that have shaped niche perfume discovery for years — not because the bottles are different inside the box, but because the box itself frames the bottle as worthy of ceremony. The ritual rewrites the product.

A bottle handed to you in a paper bag becomes a commodity. The same bottle, lifted from a velvet-lined drawer beneath layers of black tissue, becomes an object you will keep on a shelf for a decade.

## Why slow matters

The most refined unboxing experiences in perfumery share three traits:

1. **Resistance.** Nothing opens too easily. A magnetic flap, a wax seal, a knotted ribbon — each small obstacle invites the hand to slow down.
2. **Layering.** Box → sleeve → tissue → bottle. Each layer is a curtain. Without layers, there is no reveal.
3. **Material honesty.** Heavy paper, real cloth, glass that sits cold in the palm. Plastic interrupts the spell.

When all three are present, opening a perfume becomes its own short ceremony — measured in seconds, but remembered for years.

## The first spray, framed

By the time the cap is lifted, the nose is no longer naive. It has been prepared by the eyes and the hands. The top notes — citrus, aldehyde, green leaf — are received not as chemicals but as the conclusion of a sentence the box began.

This is what [Lecmo](/) means when we say a fragrance is a ritual, not a product. The box is not a container. It is the first paragraph.`,
    },
    ar: {
      title: "طقس الفتح: لماذا يبدأ العطر قبل أوّل رشّة",
      excerpt:
        "في عالم العطور الفاخرة، الصندوق ليس تغليفاً — إنّه الفصل الأوّل. إليك لماذا تُحدِّد طريقة فتح القارورة كيف ستشمّها.",
      body: `في العطور الفاخرة، تجربة العطر لا تبدأ على البشرة. تبدأ عند الباب — مع وزن الطرد، ومقاومة الشريط، وصوت الورق الناعم وهو ينفتح. قبل أوّل رشّة بكثير، يكون جهازك العصبيّ قد قرّر بالفعل ما إذا كان ما في الداخل ثميناً أم عاديّاً.

هذا ليس مبالغة شاعريّة. لحظة الـunboxing حدثٌ نفسيٌّ قابل للقياس. حين يبدو الطرد مدروساً — حوافٌّ مختومة، كرتونٌ متماسك، بطانةٌ من قماش — يفسّر الدماغ هذه المقاومة على أنّها إشارة قيمة. كلّ ثانية من الكشف البطيء تبني الترقّب، والترقّب — عصبيّاً — أقرب إلى المتعة من الكشف نفسه.

## هندسة الترقّب

تفهم دور العطور الفاخرة هذا بالحدس. بَنَت Harrods ثقافة كاملة من فيديوهات «كشف العطر» شكّلت اكتشاف العطور النيتش لسنوات — ليس لأنّ القوارير مختلفة داخل الصندوق، بل لأنّ الصندوق نفسه يُؤطِّر القارورة كشيءٍ يستحقّ مراسم. الطقس يُعيد كتابة المنتج.

قارورة تُسلَّم لك في كيس ورقيّ تصبح سلعةً. القارورة ذاتها، مرفوعةً من درجٍ مبطَّن بالمخمل تحت طبقاتٍ من الورق الأسود، تصبح غرضاً ستحتفظ به على رفّ لعقد كامل.

## لماذا البطء مهمّ

تجارب الفتح الأرقى في العطور تتشارك ثلاث صفات:

1. **المقاومة.** لا شيء يُفتَح بسهولةٍ زائدة. رفرفٌ مغناطيسيّ، ختمٌ من الشمع، شريطٌ معقود — كلّ عائقٍ صغير يدعو اليد لتُبطئ.
2. **التطبيق.** صندوق ← غلاف ← ورق ← قارورة. كلّ طبقةٍ ستارة. بلا طبقات، لا كشف.
3. **صدق المادّة.** ورقٌ ثقيل، قماشٌ حقيقيّ، زجاجٌ يستقرّ بارداً في راحة اليد. البلاستيك يقطع السحر.

حين تتوفّر الثلاث، يصبح فتح عطرٍ مراسمَ صغيرة بحدّ ذاتها — تُقاس بالثواني، لكنّها تُذكَر لسنوات.

## أوّل رشّة، مُؤطَّرة

بحلول الوقت الذي يُرفَع فيه الغطاء، لم يعد الأنف ساذجاً. لقد جُهِّز بالعينين واليدين. النوتات العليا — الحمضيّات، الألدهيد، الورقة الخضراء — تُستقبَل لا كموادَّ كيميائيّة بل كخاتمة جملةٍ بدأها الصندوق.

هذا ما تعنيه [Lecmo](/) حين نقول إنّ العطر طقسٌ لا منتج. الصندوق ليس وعاءً. إنّه الفقرة الأولى.`,
    },
    sources: [],
  },

  {
    slug: "inside-the-lecmo-box",
    category: "ritual",
    minutes: 5,
    publishedAt: "2026-04-15",
    coverImage: editorial1,
    en: {
      title: "Inside the Lecmo Box: An Anatomy of the First Moment",
      excerpt:
        "A walk through what arrives at your door — the textures, the order of reveal, and the small details that turn a delivery into a ceremony.",
      body: `Every [Lecmo bottle](/shop) leaves the atelier inside a box that we built — not chose. The box is part of the formula.

## What you receive

A heavy outer sleeve, matte and cool. Inside, a structured rigid box that opens from the side rather than the top — a small departure from the expected, designed to slow the hand. Black tissue, hand-folded. Beneath it, the bottle, set into a velvet-lined cradle that holds it upright like a small monument.

A handwritten card sits beside it, blank if you did not request a message, written by hand if you did. We do not print signatures.

## The order matters

The reveal is choreographed: sleeve, lid, tissue, bottle, card. Five steps. Each one buys two seconds. Ten seconds total — long enough for the mind to settle, short enough that anticipation never tips into impatience.

Studies of luxury packaging consistently show the same thing: the perceived value of a product rises with the time spent receiving it, up to roughly fifteen seconds. After that, friction becomes annoyance. We aimed below the ceiling.

## Why velvet, why black

Velvet is chosen for one reason: it absorbs sound. When the bottle leaves its cradle, there is no rattle. Silence around an object is one of the oldest signals of preciousness — museums use it, jewellers use it, and now your kitchen counter does too.

Black is chosen because it disappears. The eye is not asked to look at the box; it is asked to look at the bottle. The packaging is the frame, not the painting.

## The card

If your order is a gift, the card carries your handwritten message — transcribed by a member of our team in calligraphic script. We do not ship pre-printed greetings. The reasoning is simple: a printed message announces a transaction; a handwritten one announces a relationship.

This is what we mean by "wrapped like a secret." Everything in the box is engineered to make the moment quiet, and to make the quiet feel intentional.`,
    },
    ar: {
      title: "في [علبة Lecmo](/shop): تشريحٌ للّحظة الأولى",
      excerpt:
        "جولةٌ فيما يصل إلى بابك — الملامس، ترتيب الكشف، والتفاصيل الصغيرة التي تحوّل توصيلاً إلى مراسم.",
      body: `كلّ [قارورة Lecmo](/shop) تغادر المعمل داخل علبةٍ بنيناها — لم نخترها. العلبة جزءٌ من التركيبة.

## ما يصلك

غلافٌ خارجيّ ثقيل، مطفأ وبارد الملمس. في الداخل، علبةٌ صلبة منظَّمة تُفتَح من الجانب لا من الأعلى — انحرافٌ صغير عن المتوقَّع، مصمَّم لإبطاء اليد. ورقٌ أسود مطويٌّ يدويّاً. تحته، القارورة، مغروسةٌ في مهدٍ مبطَّن بالمخمل يُمسكها واقفةً كنُصبٍ صغير.

بطاقةٌ مكتوبة بخطّ اليد بجانبها، فارغةٌ إن لم تطلب رسالة، مكتوبةٌ يدويّاً إن طلبت. نحن لا نطبع التوقيعات.

## الترتيب يهمّ

الكشف مُصمَّم بدقّة: غلاف، غطاء، ورق، قارورة، بطاقة. خمس خطوات. كلّ واحدةٍ تشتري ثانيتين. عشر ثوانٍ إجمالاً — طويلة بما يكفي ليستقرّ الذهن، وقصيرة بما يكفي ألّا ينقلب الترقّب إلى نفاد صبر.

تُظهر دراسات التغليف الفاخر الأمر ذاته باستمرار: القيمة المُدرَكة لمنتجٍ ترتفع مع الوقت المُستغرَق في استلامه، حتّى نحو خمس عشرة ثانية. بعد ذلك، تتحوّل المقاومة إلى ضيق. صوّبنا تحت السقف.

## لماذا المخمل، لماذا الأسود

اختير المخمل لسببٍ واحد: يمتصّ الصوت. حين تغادر القارورة مهدها، لا خشخشة. الصمت حول شيءٍ ما هو من أقدم إشارات النفاسة — تستخدمه المتاحف، يستخدمه الجواهريّون، والآن يستخدمه طاولة مطبخك أيضاً.

اختير الأسود لأنّه يختفي. لا يُطلَب من العين أن تنظر إلى العلبة؛ يُطلَب منها أن تنظر إلى القارورة. التغليف إطار، لا اللوحة.

## البطاقة

إن كان طلبك هديّة، تحمل البطاقة رسالتك المكتوبة بخطّ اليد — منسوخةً بيد عضوٍ من فريقنا بخطٍّ خطّاطيّ. لا نشحن تحيّاتٍ مطبوعةً مسبقاً. السبب بسيط: الرسالة المطبوعة تُعلِن معاملة، والمكتوبة بخطّ اليد تُعلِن علاقة.

هذا ما نعنيه بـ«مُغلَّفةٌ كسرّ». كلّ شيءٍ في العلبة مُهندَسٌ ليجعل اللحظة هادئة، ويجعل الهدوء يبدو مقصوداً.`,
    },
    sources: [],
  },

  // ── House / Lecmo Story ────────────────────
  {
    slug: "why-lecmo",
    category: "house",
    minutes: 7,
    publishedAt: "2026-04-18",
    coverImage: editorial2,
    en: {
      title: "Why Lecmo: A Gulf Perfume House, Composed in a Modern Tongue",
      excerpt:
        "Born in the Arabian Gulf, written for the present. The reasons we built Lecmo, and the lineage we are answering.",
      body: `The Gulf is one of the oldest perfume cultures in the world. Long before perfumery became a French industry, the souks of the Arabian peninsula traded oud, rose, amber and musk as currency. Fragrance here was not a luxury added to life — it was the air of life itself.

[Lecmo](/) was built at the intersection of that lineage and a contemporary instinct: to compose perfumes that respect the depth of Khaleeji tradition while speaking in a language the present can wear without translation.

## The lineage

In the UAE and Saudi Arabia, scent is woven into ritual. A guest is welcomed with bukhoor before coffee is served. A bride is perfumed for days before her wedding. Personal fragrance is layered — oil, mist, smoke — into a signature that announces a person before they speak. According to cultural studies of Gulf fragrance traditions, the average household in Saudi Arabia and the UAE spends significantly more on perfume per capita than any other region in the world.

This is the air Lecmo was born into. It is not a market we entered; it is a culture we belong to.

## The contemporary instinct

But to honour a tradition is not to repeat it. The classical Khaleeji perfume — heavy on attar oils, dense oud, smoky resins — was composed for a slower world, often layered over hours and worn close to the body. The present asks for something else: compositions that carry the same depth but breathe more lightly, that move from a meeting to a dinner without overwhelming the room.

[Lecmo's compositions](/shop) are built around this tension. Real oud, real rose, real amber — but balanced with modern accords (clean musks, transparent woods, soft leathers) that let the heart of the fragrance speak without shouting.

## Why a new house

The Gulf does not need another perfume brand. It needed a house that could hold both ends of the rope: the heritage and the now. A house that would not dilute oud to please the West, and would not refuse modernity to flatter the past.

Lecmo is our attempt at that house. Composed in the Gulf. Written for the present. Wrapped like a secret.`,
    },
    ar: {
      title: "لماذا Lecmo: دار عطور خليجيّة، تُؤلَّف بلسانٍ معاصر",
      excerpt:
        "وُلِدَت في الخليج العربيّ، كُتِبَت للحاضر. الأسباب التي بَنَيْنا Lecmo لأجلها، والسلالة التي نجيب عليها.",
      body: `الخليج من أقدم ثقافات العطر في العالم. قبل أن تصبح العطور صناعةً فرنسيّة بزمنٍ طويل، كانت أسواق الجزيرة العربيّة تتاجر بالعود والورد والعنبر والمسك كعملة. العطر هنا لم يكن ترفاً يُضاف إلى الحياة — كان هواء الحياة نفسه.

بُنِيَت [Lecmo](/) عند تقاطع تلك السلالة وحدسٍ معاصر: أن نؤلّف عطوراً تحترم عمق التقليد الخليجيّ مع التحدّث بلغةٍ يستطيع الحاضر ارتداؤها دون ترجمة.

## السلالة

في الإمارات والسعوديّة، الرائحة منسوجةٌ في الطقس. يُستقبَل الضيف بالبخور قبل تقديم القهوة. تُعطَّر العروس لأيّامٍ قبل زفافها. العطر الشخصيّ مُطبَّق بطبقات — زيت، رذاذ، دخان — في توقيعٍ يُعلِن الشخص قبل أن يتكلّم. وفقاً لدراسات ثقافيّة عن تقاليد العطور الخليجيّة، يُنفِق متوسّط الأسرة في السعوديّة والإمارات على العطور للفرد أكثر بشكلٍ ملحوظ من أيّ منطقةٍ أخرى في العالم.

هذا هو الهواء الذي وُلِدَت فيه Lecmo. ليس سوقاً دخلناه؛ إنّه ثقافةٌ ننتمي إليها.

## الحدس المعاصر

لكنّ تكريم تقليدٍ ليس تكراره. العطر الخليجيّ الكلاسيكيّ — الثقيل بزيوت العطر، الكثيف بالعود، المُدخَّن بالرتنجات — أُلِّف لعالمٍ أبطأ، يُطبَّق غالباً على ساعاتٍ ويُلبَس قريباً من الجسد. يطلب الحاضر شيئاً آخر: مؤلّفاتٍ تحمل العمق ذاته لكن تتنفّس أخفّ، تنتقل من اجتماعٍ إلى عشاءٍ دون أن تطغى على الغرفة.

مؤلّفات Lecmo مبنيّةٌ حول هذا التوتّر. عودٌ حقيقيّ، وردٌ حقيقيّ، عنبرٌ حقيقيّ — لكن متوازنٌ مع توافقاتٍ حديثة (مسكٌ نظيف، أخشابٌ شفّافة، جلودٌ ناعمة) تتيح لقلب العطر أن يتكلّم دون صراخ.

## لماذا دارٌ جديدة

لا يحتاج الخليج إلى علامة عطورٍ أخرى. كان يحتاج إلى دارٍ قادرةٍ على إمساك طرفَي الحبل: التراث والآن. دارٌ لا تُخفِّف العود لإرضاء الغرب، ولا ترفض الحداثة لمداهنة الماضي.

Lecmo محاولتنا لتلك الدار. مُؤلَّفة في الخليج. مكتوبة للحاضر. مُغلَّفة كسرّ.`,
    },
    sources: [],
  },

  {
    slug: "slow-perfumery",
    category: "house",
    minutes: 6,
    publishedAt: "2026-04-22",
    coverImage: journalAtelier,
    en: {
      title: "Slow Perfumery: Why We Compose in Small Batches",
      excerpt:
        "The industry compresses fragrance creation into weeks. We measure ours in seasons. Here is why time is the most expensive ingredient.",
      body: `The mainstream fragrance industry has spent the last decade compressing scent creation into briefing decks, algorithmic consumer testing, and three-week production cycles. [Lecmo](/) runs in the opposite direction.

## What "slow" means in practice

A Lecmo composition is built over months, not weeks. The first sketch of an accord may sit on the perfumer's desk for sixty days before it is touched again. The maturation — the period during which the finished blend rests in glass before being bottled — is measured in weeks, not days. Some natural perfumers work to even longer cycles; the natural perfumer Hélène Aubert, in her Grasse atelier, has spoken publicly about single fragrances that take six months to be born.

We do not work to that extreme. But we refuse the three-week ceiling that mass production demands.

## Why time is an ingredient

Perfume is a chemistry of patience. When raw materials are first combined, they fight. Top notes scream over base notes. Aldehydes overwhelm florals. Oud, in particular, takes time to settle into the woods around it.

Maceration is the period during which those battles resolve. The longer a composition rests, the more its components agree with each other. A two-week macerated blend smells like ingredients in a bottle. A six-week macerated blend smells like a perfume.

This is not a marketing claim. It is the reason small-batch perfumery consistently outperforms mass production on blind tests of complexity and longevity. The juice is more married. The notes hand off to each other instead of arguing.

## The economics of small

Working in small batches is more expensive per bottle. There is no cost advantage to making 200 units instead of 200,000. But small batches allow three things mass production cannot:

1. **Real materials.** When you make less, you can afford better. Real Bulgarian rose absolute. Real aged oud oil. Real ambergris tincture.
2. **Quality control by hand.** Every bottle is checked, capped, and labelled by a person.
3. **Course correction.** If a batch is not right, it does not ship. There is no commitment to a 50,000-unit production run.

## Why this matters for you

The [Lecmo bottle](/shop) in your hand was probably made within the last ninety days. It was probably part of a batch of fewer than five hundred. The same hands that mixed it likely also packed it.

This is not nostalgia. It is the only honest way we know to make a perfume worth keeping.`,
    },
    ar: {
      title: "العطور البطيئة: لماذا نُؤلِّف بكمّيّاتٍ صغيرة",
      excerpt:
        "تضغط الصناعة خلق العطور في أسابيع. نحن نقيس عطورنا بالفصول. إليك لماذا الوقت هو أغلى مكوّن.",
      body: `أمضت صناعة العطور السائدة العقد الأخير في ضغط خلق العطور إلى عروض موجزات، واختبارات مستهلكين خوارزميّة، ودورات إنتاج من ثلاثة أسابيع. تعمل [Lecmo](/) في الاتجاه المعاكس.

## ماذا يعني «البطيء» عمليّاً

تُبنَى مؤلّفة Lecmo على أشهرٍ لا أسابيع. قد ترقد المسوّدة الأولى لتوافقٍ على طاولة العطّار ستّين يوماً قبل لمسها مجدّداً. النضوج — الفترة التي ترقد فيها الخلطة الجاهزة في الزجاج قبل تعبئتها — يُقاس بالأسابيع لا الأيّام. بعض العطّارين الطبيعيّين يعملون بدوراتٍ أطول؛ العطّارة الطبيعيّة Hélène Aubert، في معملها بـGrasse، تحدّثت علناً عن عطورٍ مفردة تستغرق ستّة أشهر لتولد.

نحن لا نعمل بهذا التطرّف. لكنّنا نرفض سقف الأسابيع الثلاثة الذي يفرضه الإنتاج الضخم.

## لماذا الوقت مكوّن

العطر كيمياء صبر. حين تُمزَج المواد الخام أوّل مرّة، تتقاتل. تصرخ النوتات العليا فوق القاعدة. يطغى الألدهيد على الزهور. العود، خاصّةً، يأخذ وقتاً ليستقرّ في الأخشاب حوله.

النضوج هي الفترة التي تُحلّ فيها تلك المعارك. كلّما رقدت المؤلّفة أطول، اتّفقت مكوّناتها أكثر. خلطةٌ نضجت أسبوعين تشمّ كمكوّناتٍ في قارورة. خلطةٌ نضجت ستّة أسابيع تشمّ كعطر.

هذا ليس ادّعاءً تسويقيّاً. هذا هو السبب وراء تفوّق العطور صغيرة الكمّيّة باستمرار على الإنتاج الضخم في اختبارات التعقيد والثبات. الخلطة أكثر تزاوجاً. النوتات تُسلِّم لبعضها بدل التشاجر.

## اقتصاديّات الصغير

العمل بكمّيّاتٍ صغيرة أغلى لكلّ قارورة. لا توجد ميزة تكلفة في صنع ٢٠٠ وحدة بدل ٢٠٠٫٠٠٠. لكنّ الكمّيّات الصغيرة تتيح ثلاثة أشياء لا يستطيعها الإنتاج الضخم:

١. **مواد حقيقيّة.** حين تصنع أقلّ، تستطيع تحمّل الأفضل. مطلق ورد بلغاريّ حقيقيّ. زيت عودٍ معتَّق حقيقيّ. صبغة عنبرٍ خامٍ حقيقيّة.
٢. **تحكّم في الجودة باليد.** كلّ قارورة مُتحقَّق منها، مُغلَّقة، ومُعنوَنة بيد إنسان.
٣. **تصحيح المسار.** إن لم تكن الدفعة صحيحة، لا تُشحَن. لا التزام بدفعة إنتاج ٥٠٫٠٠٠ وحدة.

## لماذا هذا يهمّك

[قارورة Lecmo](/shop) في يدك صُنِعَت غالباً خلال التسعين يوماً الماضية. كانت غالباً جزءاً من دفعةٍ أقلّ من خمسمئة. الأيدي ذاتها التي مزجتها هي على الأغلب التي عبّأتها.

هذا ليس حنيناً. هذه هي الطريقة الصادقة الوحيدة التي نعرفها لصنع عطرٍ يستحقّ الاحتفاظ به.`,
    },
    sources: [],
  },

  // ── Craft / Niche Perfumery ────────────────
  {
    slug: "niche-vs-designer",
    category: "craft",
    minutes: 8,
    publishedAt: "2026-04-25",
    coverImage: editorial3,
    en: {
      title: "Niche vs Designer: What the Difference Really Is",
      excerpt:
        "The word \"niche\" has been stretched until it means nothing. Here is the honest, structural difference — and why it matters.",
      body: `The word "niche" has been used so loosely in fragrance marketing that it now risks meaning nothing. Influencers call mass-distributed brands "niche." Department stores build "niche corners" stocked with labels owned by the same three conglomerates. The line has been blurred on purpose, because the word sells.

What follows is an attempt to restore the edges.

## The structural difference

A **designer fragrance** is a perfume produced by a fashion or lifestyle brand whose primary business is not perfume. The fragrance is a license — often outsourced to a beauty conglomerate (Coty, Inter Parfums, L'Oréal) — designed to extend the brand's reach into a high-margin, low-cost category. Production runs are measured in millions of units. Marketing budgets often exceed development budgets ten to one.

A **niche fragrance**, in its honest definition, is a perfume produced by a house whose primary — and often only — business is perfume. Production runs are measured in hundreds or low thousands. The perfumer has direct creative control. Distribution is intentionally limited.

## What this means for the bottle

In a designer fragrance, the bottle is the asset and the juice is the cost. Materials are chosen for stability, scalability, and a price point that supports a global advertising campaign. Synthetic substitutes replace natural materials wherever possible.

In a niche fragrance, the juice is the asset and the bottle is the frame. Materials can be more expensive because the production run is smaller and the margin is structurally different. Naturals — real rose absolute, real oud, real iris butter — can be used at meaningful concentrations.

## The market reality

The numbers confirm the divergence. According to industry analysis, niche perfume sales have grown at 12–13% annually in recent years, compared to 2–5% for traditional designer brands. Fragrances priced over €150 grew by 14% in 2025, while mid-range designers (€50–€150) declined by 3%. Esxence, the major niche perfumery trade show in Milan, hosted nearly 400 brands and 13,000 visitors in 2025 — a clear sign of where the energy is moving.

## What to ignore

Two false signals dominate the conversation:

1. **Price.** Many designer fragrances cost as much as niche ones. Price is a marketing decision, not a structural one.
2. **The word "exclusive."** Used by every brand. Means nothing in itself.

## What to look for

1. **Who owns the house?** If the answer is a beauty conglomerate, it is structurally a designer fragrance regardless of the marketing.
2. **Who composes the perfumes?** A named in-house perfumer with creative control is a niche signal. An anonymous "fragrance team" is usually outsourced.
3. **How many SKUs?** Niche houses tend to maintain small, edited collections. A brand with sixty fragrances is almost never structurally niche.
4. **Where can you buy it?** Limited, controlled distribution is a niche signal. Global airport availability is not.

## Where [Lecmo](/) sits

Lecmo is structurally niche. The house is independent. The perfumer is in the room. Production runs are small. Distribution is direct. The same person who composed the fragrance you wear can answer the email you send.

That is the difference, in one sentence.`,
    },
    ar: {
      title: "Niche مقابل Designer: ما الفرق فعلاً؟",
      excerpt:
        "مُطّت كلمة «نيتش» حتّى لم تعد تعني شيئاً. إليك الفرق البنيويّ الصادق — ولماذا يهمّ.",
      body: `استُخدِمَت كلمة «نيتش» في تسويق العطور بمرونةٍ شديدة لدرجة أنّها الآن تخاطر بألّا تعني شيئاً. يصف المؤثّرون علاماتٍ موزَّعة بكثافة بأنّها «نيتش». تبني المتاجر الكبرى «أركان نيتش» مكدَّسةً بعلاماتٍ تملكها الشركات الثلاث الكبرى ذاتها. طُمِسَ الخطّ عن قصد، لأنّ الكلمة تبيع.

ما يلي محاولةٌ لاستعادة الحدود.

## الفرق البنيويّ

**عطر Designer** هو عطرٌ تُنتِجه علامةٌ للأزياء أو نمط الحياة، لكنّ نشاطها الرئيسيّ ليس العطور. العطر ترخيص — يُستعان غالباً فيه بشركةٍ كبرى للتجميل (Coty، Inter Parfums، L'Oréal) — مصمَّم لتوسيع وصول العلامة إلى فئةٍ عالية الهامش، منخفضة التكلفة. تُقاس دفعات الإنتاج بالملايين من الوحدات. كثيراً ما تتجاوز ميزانيّات التسويق ميزانيّات التطوير بعشرة أضعاف.

**عطر Niche**، في تعريفه الصادق، هو عطرٌ تُنتِجه دارٌ نشاطها الرئيسيّ — وغالباً الوحيد — هو العطور. تُقاس دفعات الإنتاج بالمئات أو الآلاف القليلة. للعطّار تحكّمٌ إبداعيّ مباشر. التوزيع محدودٌ بقصد.

## ماذا يعني هذا للقارورة

في عطر Designer، القارورة هي الأصل والخلطة هي التكلفة. تُختار المواد لأجل الاستقرار، القابليّة للتوسّع، ونقطة سعرٍ تدعم حملةً إعلانيّة عالميّة. تحلّ البدائل الصناعيّة محلّ المواد الطبيعيّة أينما أمكن.

في عطر Niche، الخلطة هي الأصل والقارورة هي الإطار. يمكن للمواد أن تكون أغلى لأنّ دفعة الإنتاج أصغر والهامش بنيويّاً مختلف. يمكن استخدام الطبيعيّات — مطلق وردٍ حقيقيّ، عودٌ حقيقيّ، زبدة سوسنٍ حقيقيّة — بتراكيز ذات معنى.

## واقع السوق

تؤكّد الأرقام التباعد. وفقاً لتحليل الصناعة، نمت مبيعات العطور النيتش بمعدّل ١٢–١٣٪ سنويّاً في السنوات الأخيرة، مقارنةً بـ٢–٥٪ للعلامات Designer التقليديّة. نمت العطور التي تتجاوز ١٥٠ يورو بنسبة ١٤٪ في ٢٠٢٥، بينما تراجعت العطور المتوسّطة (٥٠–١٥٠ يورو) بنسبة ٣٪. استضاف معرض Esxence، أبرز معرض للعطور النيتش في ميلان، نحو ٤٠٠ علامةٍ و١٣٫٠٠٠ زائرٍ في ٢٠٢٥ — إشارةٌ واضحة إلى أين تتحرّك الطاقة.

## ما يجب تجاهله

تهيمن إشارتان زائفتان على المحادثة:

١. **السعر.** كثيرٌ من عطور Designer تكلّف بقدر النيتش. السعر قرار تسويقيّ، لا بنيويّ.
٢. **كلمة «حصريّ».** تُستخدَم من كلّ علامة. لا تعني شيئاً بذاتها.

## ما يجب البحث عنه

١. **من يملك الدار؟** إن كان الجواب شركةً كبرى للتجميل، فهو بنيويّاً عطر Designer بصرف النظر عن التسويق.
٢. **من يُؤلّف العطور؟** عطّارٌ داخليّ مُسمَّى مع تحكّم إبداعيّ إشارةُ نيتش. «فريق عطور» مجهول عادةً مُستعان به.
٣. **كم منتجاً (SKU)؟** تميل دور النيتش إلى الحفاظ على مجموعاتٍ صغيرة منقّحة. علامةٌ بستّين عطراً تكاد لا تكون بنيويّاً نيتش.
٤. **أين تشتريه؟** التوزيع المحدود والمُتحكَّم به إشارةُ نيتش. التوفّر في مطارات العالم ليس كذلك.

## أين تقع [Lecmo](/)

Lecmo بنيويّاً نيتش. الدار مستقلّة. العطّار في الغرفة. دفعات الإنتاج صغيرة. التوزيع مباشر. الشخص ذاته الذي ألّف العطر الذي ترتديه قادرٌ على الردّ على إيميلٍ ترسله.

هذا هو الفرق، في جملةٍ واحدة.`,
    },
    sources: [],
  },

  {
    slug: "the-fragrance-pyramid",
    category: "craft",
    minutes: 7,
    publishedAt: "2026-04-28",
    coverImage: journalAtelier,
    en: {
      title: "The Fragrance Pyramid: How to Read Top, Heart, and Base Notes",
      excerpt:
        "A perfume is a sentence with a beginning, middle, and end. Here is how to read its three layers — and why the order matters.",
      body: `Most people smell a perfume the way they read a tweet: one quick inhale, a yes or no, done. But fragrance is not a single moment. It is a structured arc — a sentence with a beginning, a middle, and an end — and learning to read it changes the way you choose, wear, and remember a scent.

That arc has a name in perfumery: the **fragrance pyramid**.

## What the pyramid is

Every composed perfume is built in three stacked layers, defined by how quickly each ingredient evaporates from the skin:

1. **Top notes** (the opening) — the smallest, lightest molecules. They evaporate within 5 to 15 minutes. Citrus, herbs, aldehydes, green leaves.
2. **Heart notes** (the body) — middle-weight molecules. They emerge after the top notes burn off and last 2 to 4 hours. Florals, spices, fruits, soft woods.
3. **Base notes** (the dry-down) — the heaviest, longest-lasting molecules. They start to assert after the heart fades and can persist on skin for 6 to 24 hours. Oud, amber, musk, vanilla, leather, resins.

The names you see on a bottle's note list are arranged in this order for a reason: they describe the order in which you will smell them.

## Why the opening lies

The most common mistake in choosing a perfume is buying it at the counter on the strength of the first thirty seconds. That moment belongs to the top notes — and the top notes are designed to be charming, not lasting. They are the perfume's introduction, not its character.

A perfume should be judged at minute 30 (when the heart is fully open) and again at hour 4 (when the base has settled into your skin). What you smell then is the perfume you will live with.

## How to test a perfume properly

The professional method is simple and free:

1. Spray on the inside of your wrist. Do not rub. Rubbing breaks the top molecules and distorts the opening.
2. Walk away. Read a magazine. Have coffee. Do not smell it for 20 minutes.
3. Return to your wrist. The heart is now speaking. This is the perfume's personality.
4. Check again three hours later. The base is now the loudest voice. This is what your clothes will hold tomorrow.

If you love it at all three checkpoints, buy it. If only at the first, walk away.

## Reading the pyramid as a map

Once you know the structure, a note list becomes legible:

- A perfume listed as "bergamot, jasmine, sandalwood" tells you: bright opening, classic floral heart, soft and creamy dry-down. Probably elegant, probably balanced.
- A perfume listed as "saffron, rose, oud" tells you: spicy opening, deep floral heart, intense and resinous dry-down. Probably warm, probably bold, probably long-lasting.

This is not a guess. It is a reading.

## Why this matters

The pyramid is not perfumer's jargon. It is a tool for the wearer. Once you can read it, you stop being persuaded by the first impression and start choosing perfumes by what they will say all day.

That is the moment you go from buying scents to wearing fragrance.`,
    },
    ar: {
      title: "هرم العطر: كيف تقرأ النوتات العليا، القلب، والقاعدة",
      excerpt:
        "العطر جملةٌ لها بداية ووسط ونهاية. إليك كيف تقرأ طبقاته الثلاث — ولماذا الترتيب يهمّ.",
      body: `يشمّ معظم الناس العطر بطريقة قراءتهم لتغريدة: شهيقٌ سريع، نعم أو لا، انتهى. لكنّ العطر ليس لحظةً واحدة. إنّه قوسٌ منظَّم — جملةٌ لها بداية، وسط، ونهاية — وتعلّم قراءته يُغيّر طريقة اختيارك، ارتدائك، وتذكّرك للعطر.

ذلك القوس له اسمٌ في العطور: **هرم العطر**.

## ما هو الهرم

كلّ عطرٍ مُؤلَّف مبنيٌّ من ثلاث طبقاتٍ مكدَّسة، تُعرَّف بسرعة تبخّر كلّ مكوّنٍ من البشرة:

١. **النوتات العليا** (الافتتاحيّة) — أصغر الجزيئات وأخفّها. تتبخّر خلال ٥ إلى ١٥ دقيقة. حمضيّات، أعشاب، ألدهيدات، أوراقٌ خضراء.
٢. **نوتات القلب** (الجسد) — جزيئاتٌ متوسّطة الوزن. تظهر بعد احتراق النوتات العليا وتدوم ٢ إلى ٤ ساعات. زهور، توابل، فواكه، أخشابٌ ناعمة.
٣. **نوتات القاعدة** (الجفاف النهائيّ) — أثقل الجزيئات وأطولها بقاءً. تبدأ بالظهور بعد تلاشي القلب وقد تستمرّ على البشرة من ٦ إلى ٢٤ ساعة. عود، عنبر، مسك، فانيليا، جلد، رتنجات.

الأسماء التي تراها في قائمة نوتات القارورة مرتَّبة بهذا الترتيب لسبب: تصف الترتيب الذي ستشمّها به.

## لماذا تكذب الافتتاحيّة

أكثر الأخطاء شيوعاً في اختيار عطر هو شراؤه من الكاونتر بناءً على قوّة الثلاثين ثانية الأولى. تلك اللحظة تخصّ النوتات العليا — والنوتات العليا مصمَّمة لتكون ساحرة، لا دائمة. هي تقديم العطر، لا شخصيّته.

يجب الحكم على العطر في الدقيقة ٣٠ (حين يكون القلب منفتحاً تماماً) ومجدّداً في الساعة ٤ (حين تستقرّ القاعدة على بشرتك). ما تشمّه حينها هو العطر الذي ستعيش معه.

## كيف تختبر عطراً بشكلٍ صحيح

الطريقة الاحترافيّة بسيطة ومجّانيّة:

١. رشّ على باطن معصمك. لا تفرك. الفرك يكسر جزيئات الافتتاحيّة ويُشوّه البداية.
٢. ابتعد. اقرأ مجلّة. اشرب قهوة. لا تشمّه لـ٢٠ دقيقة.
٣. عُد إلى معصمك. القلب يتكلّم الآن. هذه شخصيّة العطر.
٤. تحقّق مجدّداً بعد ثلاث ساعات. القاعدة هي الصوت الأعلى الآن. هذا ما ستحمله ملابسك غداً.

إن أحببته في النقاط الثلاث، اشتره. إن في الأولى فقط، ابتعد.

## قراءة الهرم كخريطة

حين تعرف البنية، تصبح قائمة النوتات قابلةً للقراءة:

- عطر مدرجٌ بـ«برغموت، ياسمين، صندل» يخبرك: افتتاحيّةٌ مُشرِقة، قلبٌ زهريّ كلاسيكيّ، جفافٌ ناعمٌ كريميّ. على الأرجح أنيق، على الأرجح متوازن.
- عطر مدرجٌ بـ«زعفران، ورد، عود» يخبرك: افتتاحيّةٌ حارّة، قلبٌ زهريٌّ عميق، جفافٌ كثيفٌ راتنجيّ. على الأرجح دافئ، على الأرجح جريء، على الأرجح طويل البقاء.

هذا ليس تخميناً. هذه قراءة.

## لماذا هذا يهمّ

الهرم ليس مصطلح عطّارين. إنّه أداةٌ للمُرتدي. حين تستطيع قراءته، تتوقّف عن أن تُقنَع بالانطباع الأوّل وتبدأ باختيار العطور بناءً على ما ستقوله طوال اليوم.

تلك هي اللحظة التي تنتقل فيها من شراء الروائح إلى ارتداء العطر.`,
    },
    sources: [],
  },

  {
    slug: "oud-from-tree-to-bottle",
    category: "craft",
    minutes: 9,
    publishedAt: "2026-05-01",
    coverImage: journalOud,
    en: {
      title: "Oud, From Tree to Bottle: A Perfumer's Field Guide",
      excerpt:
        "The world's most expensive perfumery raw material is also its most misunderstood. A grounded look at oud — its origin, its grades, and its honesty.",
      body: `Oud is the most expensive widely-used raw material in modern perfumery. Wholesale prices for true distilled oud oil range from approximately USD 3,000 to over USD 40,000 per kilogram, depending on grade and provenance. To understand what you are paying for — and what you are wearing — you have to understand where it comes from.

## The biology

Oud (also written *oudh* or *aoud*; called *agarwood* in English-language botany) is a dark, resinous heartwood produced by trees of the *Aquilaria* and *Gyrinops* genera, native to South and Southeast Asia. The resin does not form in healthy trees. It is a defensive response: when the tree is wounded and infected by a specific mould, it floods the affected wood with aromatic compounds (sesquiterpenoids and chromones) to seal the damage.

Over years — sometimes decades — the infected wood transforms into agarwood. The longer the infection lives, the deeper the resin saturation, and the more valuable the wood.

## The origins

Different growing regions produce different aromatic profiles. Three of the most prized:

- **Hindi (Indian) oud** — from Assam and surrounding regions of northeast India. Animalic, dark, leathery, often described as "barnyard" in its raw form. Beloved in the Gulf, polarising elsewhere.
- **Cambodi (Cambodian) oud** — from Cambodia and parts of Laos and Vietnam. Sweeter, fruitier, with a more accessible woody warmth. Often the gateway oud for new collectors.
- **Maroke oud** — historically from the Mer region; today the term is used loosely. Sweet, cooler, with a hint of cooked fruit.

Other notable origins include Borneo, Trat (Thailand), and increasingly Assam-cultivated agarwood from sustainable plantations.

## How oud reaches a perfume

There are two main forms used in fragrance:

1. **Distilled oud oil (*dehn al oud*)** — the wood chips are soaked in water and steam-distilled, often over multiple distillation cycles. Yield is brutally low: approximately 1 to 2 kilograms of oil per 100 kilograms of agarwood chips, varying widely with grade.
2. **Oud accord (synthetic or reconstructed)** — modern perfumery often uses molecules like Z11 and Iso E Super, blended with woody and resinous synthetics to evoke the character of oud at a fraction of the cost.

Most "oud" fragrances on the global market are accords, not real oud oil. This is not necessarily dishonest — a well-built accord can be beautiful — but the difference is the same as between sparkling wine and Champagne: structurally not the same thing.

## How to tell the difference

A trained nose can tell within seconds. Untrained noses can use proxies:

- **Price.** A 50ml eau de parfum that contains meaningful amounts of real oud oil cannot retail for €60. The math does not work.
- **Longevity.** Real oud has staying power that synthetics struggle to match — particularly in the dry-down, where the resin notes deepen over hours rather than fade.
- **Complexity.** Real oud is never one note. It moves: leather, smoke, honey, animalic, sweet, dry. Accords tend to stay flatter.

## How [Lecmo](/) uses oud

We use both. Where the composition demands the depth and shift of real oil, we use it. Where a cleaner, more transparent oud character serves the perfume, we build an accord around real oud at lower concentration plus carefully chosen molecules.

Honesty matters more than purity. A perfume that uses an oud accord well is more honest than a perfume that claims "real oud" and contains a trace.

The bottle should tell the truth.`,
    },
    ar: {
      title: "العود من الشجرة إلى القارورة: دليلٌ ميدانيّ من عطّار",
      excerpt:
        "أغلى مادّة عطورٍ خامٍ مستخدمةٍ على نطاقٍ واسع في العالم هي أيضاً الأكثر سوء فهم. نظرةٌ متّزنة على العود — أصله، درجاته، وصدقه.",
      body: `العود هو أغلى مادّة خام مستخدمة على نطاقٍ واسع في العطور الحديثة. تتراوح أسعار الجملة لزيت العود المُقطَّر الحقيقيّ بين نحو ٣٫٠٠٠ و٤٠٫٠٠٠ دولارٍ أمريكيّ للكيلوغرام، تبعاً للدرجة والمصدر. لتفهم ما تدفع ثمنه — وما ترتديه — عليك أن تفهم من أين يأتي.

## البيولوجيا

العود (يُكتَب أيضاً *عودة* أو *عود*؛ ويُسمَّى *agarwood* في النباتيّات الإنجليزيّة) خشب قلبٍ داكن راتنجيّ تُنتِجه أشجارٌ من جنسَي *Aquilaria* و*Gyrinops*، الأصليّة في جنوب وجنوب شرق آسيا. لا يتشكّل الراتنج في الأشجار السليمة. إنّه استجابةٌ دفاعيّة: حين تُجرَح الشجرة وتُصاب بفطرٍ محدّد، تُغرِق الخشب المصاب بمركّباتٍ عطريّة (سيسكيتربينويدات وكرومونات) لختم الضرر.

على مدى سنوات — أحياناً عقود — يتحوّل الخشب المصاب إلى عود. كلّما عاشت الإصابة أطول، عمق تشبّع الراتنج، وارتفعت قيمة الخشب.

## الأصول

تُنتِج مناطق نموٍّ مختلفة ملامحَ عطريّةٍ مختلفة. ثلاثٌ من الأكثر نفاسة:

- **العود الهنديّ** — من آسام والمناطق المحيطة في شمال شرق الهند. حيوانيّ، داكن، جلديّ، يُوصف غالباً بـ«حظيرة» في صورته الخام. محبوبٌ في الخليج، مُستقطِبٌ في مكانٍ آخر.
- **العود الكمبوديّ** — من كمبوديا وأجزاءٍ من لاوس وفيتنام. أحلى، أكثر فاكهيّة، بدفءٍ خشبيٍّ أيسر. غالباً عود البوّابة لهواة جدد.
- **العود المروكيّ** — تاريخيّاً من منطقة مير؛ اليوم يُستخدَم المصطلح بمرونة. حلو، أبرد، بلمسة فاكهةٍ مطبوخة.

أصولٌ أخرى بارزة: بورنيو، ترات (تايلاند)، وبشكلٍ متزايد عودٌ مزروعٌ من مزارع آسام المستدامة.

## كيف يصل العود إلى عطر

هناك شكلان رئيسيّان مستخدمان في العطور:

١. **زيت العود المُقطَّر (*دهن العود*)** — تُنقَع رقائق الخشب في الماء وتُقطَّر بالبخار، غالباً عبر دوراتٍ متعدّدة. المردود قاسٍ: نحو ١ إلى ٢ كيلوغرام من الزيت لكلّ ١٠٠ كيلوغرامٍ من رقائق العود، يتفاوت بشكلٍ واسع مع الدرجة.
٢. **توافق عود (صناعيّ أو مُعاد بناؤه)** — كثيراً ما تستخدم العطور الحديثة جزيئاتٍ مثل Z11 وIso E Super، ممزوجةً بمواد خشبيّة وراتنجيّة صناعيّة لاستحضار شخصيّة العود بجزءٍ بسيط من التكلفة.

معظم عطور «العود» في السوق العالميّة توافقات، لا زيت عودٍ حقيقيّ. هذا ليس بالضرورة عدم صدق — توافقٌ مبنيٌّ جيّداً يمكن أن يكون جميلاً — لكنّ الفرق كالفرق بين النبيذ الفوّار والشمبانيا: بنيويّاً ليس الشيء ذاته.

## كيف تُميِّز

أنفٌ مُدرَّب يُميّز خلال ثوانٍ. أنوفٌ غير مُدرَّبة يمكنها استخدام دلائل:

- **السعر.** عطر ٥٠ مل يحتوي كمّيّاتٍ ذات معنى من زيت العود الحقيقيّ لا يمكن أن يُباع بـ٢٠٠ ريال. الحساب لا يعمل.
- **الثبات.** العود الحقيقيّ له بقاءٌ تكافح الصناعيّات لمضاهاته — خاصّةً في الجفاف، حيث تتعمّق نوتات الراتنج على الساعات بدل التلاشي.
- **التعقيد.** العود الحقيقيّ لا يكون نوتةً واحدة أبداً. يتحرّك: جلد، دخان، عسل، حيوانيّ، حلو، جافّ. تميل التوافقات إلى البقاء أكثر استواءً.

## كيف تستخدم [Lecmo](/) العود

نستخدم الاثنين. حيث تتطلّب المؤلّفة عمق الزيت الحقيقيّ وتحوّله، نستخدمه. حيث تخدم شخصيّة عودٍ أنظف وأشفّ العطر، نبني توافقاً حول عودٍ حقيقيّ بتركيزٍ أقلّ بالإضافة إلى جزيئاتٍ مختارةٍ بعناية.

الصدق يهمّ أكثر من النقاء. عطرٌ يستخدم توافق العود جيّداً أصدق من عطرٍ يدّعي «عوداً حقيقيّاً» ويحتوي أثراً.

يجب أن تقول القارورة الحقيقة.`,
    },
    sources: [],
  },

  // ── Gifting / Khaleeji rituals ─────────────
  {
    slug: "the-mabkhara-and-the-bottle",
    category: "gifting",
    minutes: 6,
    publishedAt: "2026-05-04",
    coverImage: journalGifting,
    en: {
      title: "The Mabkhara and the Bottle: Perfume in the Khaleeji Gift Tradition",
      excerpt:
        "In the Gulf, perfume is not a gift you give. It is a relationship you declare. A look at the cultural grammar of fragrance gifting.",
      body: `In the Gulf, perfume is not a gift in the Western sense. It is a declaration. A statement about the relationship between giver and receiver, made in a language that everyone in the room understands instantly.

To understand [Lecmo](/), you have to understand this grammar.

## The mabkhara comes first

In a Khaleeji home, the welcome of a guest follows a sequence that has not changed in centuries. Coffee. Dates. And — before or after, depending on the household — *bukhoor*: scented chips burned on charcoal in a *mabkhara* (incense burner), passed clockwise around the room. Each guest takes a moment to wave the smoke into their *ghutra*, *abaya*, or hair.

This is not decoration. It is communion. The guest leaves carrying the scent of the host's home in their clothing for hours afterward — a small, invisible thank-you that the world will smell when the guest greets the next person.

This is the cultural soil in which Khaleeji perfume gifting grew.

## The grammar of giving

When perfume is given as a gift in the Gulf, three signals are read instantly:

1. **The cost level.** Not the absolute price, but the perceived investment. A heavy bottle in a structured box says one thing; a light bottle in cellophane says another. The recipient understands the message before they smell anything.
2. **The choice of notes.** Oud says respect for elders and tradition. Rose says affection or romance. Fresh florals say youth or new friendship. Choosing the wrong note can be more awkward than choosing nothing.
3. **The presentation.** A perfume removed from its packaging and presented "ready to wear" is intimate. A perfume given in its sealed box is formal. A perfume given with handwritten calligraphy is ceremonial.

Get the grammar right and the gift speaks fluently. Get it wrong and the receiver, polite as always, will wonder what was meant.

## When perfume is given

Perfume gifting peaks around four occasions in the Gulf calendar:

- **Ramadan and Eid** — the dominant gifting season. Premium fragrance sales in the GCC consistently spike in the weeks before Eid al-Fitr and Eid al-Adha.
- **Weddings** — the bride is perfumed by her family in the days leading up to the ceremony. Perfume is exchanged between the two families as a binding gesture.
- **Births** — particularly for first sons; a gentle scent gifted to the mother.
- **Business** — the most underestimated category in the West. A signed contract in the Gulf is often closed with a fragrance gift between the principals.

## What the receiver understands

A perfume gift in the Gulf carries a specific reading regardless of the sender's intent:

- "I see you."
- "I want to be remembered when you wear this."
- "I belong to your circle."

This is why we built Lecmo's gifting flow the way we did. The handwritten card, the choice of recipient market, the sealed presentation — none of it is decorative. Each element is a word in a sentence the giver wants the receiver to read.

The bottle, in this culture, is the easy part. The grammar around it is everything.`,
    },
    ar: {
      title: "المبخرة والقارورة: العطر في تقليد الإهداء الخليجيّ",
      excerpt:
        "في الخليج، العطر ليس هديّةً تُقدِّمها. إنّه علاقةٌ تُعلِنها. نظرةٌ على القواعد الثقافيّة لإهداء العطور.",
      body: `في الخليج، العطر ليس هديّةً بالمعنى الغربيّ. إنّه إعلان. تصريحٌ عن العلاقة بين المُهدي والمُهدى إليه، مَصوغٌ بلغةٍ يفهمها كلّ من في الغرفة فوراً.

لتفهم [Lecmo](/)، عليك أن تفهم هذه القواعد.

## المبخرة تأتي أوّلاً

في البيت الخليجيّ، استقبال الضيف يتبع تسلسلاً لم يتغيّر منذ قرون. قهوة. تمر. و — قبل أو بعد، حسب البيت — *البخور*: رقائقٌ معطَّرةٌ تُحرَق على فحمٍ في *مبخرة*، تُمرَّر مع عقارب الساعة حول الغرفة. كلّ ضيفٍ يأخذ لحظةً ليُلوِّح بالدخان إلى *غترته*، *عباءته*، أو شعره.

هذا ليس زينة. إنّه تواصل. يغادر الضيف حاملاً رائحة بيت المُضيف في ملابسه لساعاتٍ بعد ذلك — شكرٌ صغيرٌ غير مرئيّ سيشمّه العالم حين يُسلِّم الضيف على الشخص التالي.

هذه هي التربة الثقافيّة التي نمت فيها هديّة العطر الخليجيّ.

## قواعد العطاء

حين يُقدَّم العطر هديّةً في الخليج، تُقرَأ ثلاث إشاراتٍ فوراً:

١. **مستوى التكلفة.** ليس السعر المطلق، بل الاستثمار المُدرَك. قارورةٌ ثقيلةٌ في علبةٍ منظَّمة تقول شيئاً؛ قارورةٌ خفيفةٌ في سيلوفان تقول آخر. يفهم المُستلِم الرسالة قبل أن يشمّ شيئاً.
٢. **اختيار النوتات.** العود يقول الاحترام لكبار السنّ والتقليد. الورد يقول المودّة أو الرومانسيّة. الزهور المنعشة تقول الشباب أو الصداقة الجديدة. اختيار النوتة الخطأ قد يكون أكثر إحراجاً من ألّا تختار شيئاً.
٣. **التقديم.** عطرٌ مُخرَجٌ من تغليفه ومُقدَّمٌ «جاهزاً للارتداء» حميميّ. عطرٌ مُقدَّمٌ في علبته المختومة رسميّ. عطرٌ مُقدَّمٌ بخطّ يدٍ خطّاطيّ مراسميّ.

اضبط القواعد ينطق العطر بطلاقة. اخطئها يتساءل المُستلِم، مهذَّباً كالعادة، ماذا كان المقصود.

## متى يُقدَّم العطر

تبلغ هديّة العطر ذروتها حول أربع مناسباتٍ في تقويم الخليج:

- **رمضان والعيد** — موسم الإهداء المهيمن. ترتفع مبيعات العطور الفاخرة في الخليج باستمرار في الأسابيع قبل عيدَي الفطر والأضحى.
- **الأعراس** — تُعطَّر العروس من قِبَل أسرتها في الأيّام التي تسبق الزفاف. يُتبادَل العطر بين الأسرتين كإيماءةٍ مُلزِمة.
- **الولادات** — خاصّةً للابن الأوّل؛ عطرٌ ناعمٌ يُهدَى للأمّ.
- **العمل** — الفئة الأكثر تقليلاً من شأنها في الغرب. عقدٌ موقَّع في الخليج كثيراً ما يُختَم بهديّة عطرٍ بين الأطراف.

## ماذا يفهم المُستلِم

تحمل هديّة العطر في الخليج قراءةً محدّدة بصرف النظر عن نيّة المُرسِل:

- «أنا أراك».
- «أريد أن أُتذكَّر حين ترتدي هذا».
- «أنتمي إلى دائرتك».

لهذا بنينا تدفّق الإهداء في Lecmo كما هو. البطاقة المكتوبة بخطّ اليد، اختيار سوق المُستلِم، التقديم المختوم — لا شيء من ذلك زينة. كلّ عنصرٍ كلمةٌ في جملةٍ يريد المُهدي أن يقرأها المُستلِم.

القارورة، في هذه الثقافة، هي الجزء السهل. القواعد حولها هي كلّ شيء.`,
    },
    sources: [],
  },

  {
    slug: "how-to-choose-a-perfume-gift",
    category: "gifting",
    minutes: 7,
    publishedAt: "2026-05-07",
    coverImage: journalGifting,
    en: {
      title: "How to Choose a Perfume as a Gift for Someone You Love",
      excerpt:
        "Choosing a fragrance for another person is the most personal gift you can give. A practical guide to getting it right.",
      body: `Perfume is the most personal gift in the world. You are not just giving an object; you are choosing the air around someone you love. Here is how to do it well.

## Start with what they already wear

The single most useful piece of information you can collect is the perfume the recipient wears now. If you can find out the name of one fragrance they love and reach for, you have your map.

That single bottle tells you:

- **Their note family.** Floral, woody, oriental, fresh, gourmand. People rarely jump families.
- **Their concentration preference.** Eau de toilette wearers tend to dislike dense parfum extracts. Parfum wearers find EdTs thin.
- **Their character.** Soft and elegant? Warm and bold? Quiet and intimate?

Then choose a fragrance from the same family but with one new dimension — a slightly deeper base, a slightly different floral, a slightly more complex spice. Same neighbourhood, new street.

## When you don't know what they wear

If you have no information, default to safe complexity. Avoid two extremes:

- **Too bland.** Generic "fresh" fragrances are forgettable, and a forgettable gift is worse than no gift.
- **Too divisive.** Strong oud, heavy leather, or animalic notes are personal. Beautiful for the right person, jarring for the wrong one.

Aim for the middle: a well-built composition with both warmth and clarity. A modern oud-rose, a soft amber, an elegant musk. These work on most people because they are universally beautiful, not because they are bland.

## Match the occasion

The right perfume changes by reason:

- **Ramadan or Eid.** Lean classical: oud, rose, amber, musk. The notes that belong to the season.
- **Wedding.** Lean elegant and floral: jasmine, white florals, soft woods. Avoid heavy gourmands.
- **Birthday for someone you know well.** Lean personal: choose a perfume that says you have been listening.
- **Business or formal gift.** Lean structured and refined: a bottle and box that signal investment without familiarity. Avoid anything overtly intimate.

## Match the body

Perfume reacts differently with different skin chemistry. Two factors matter most:

- **Skin warmth.** Warm-skinned people amplify base notes (oud, amber, musk) and project them more strongly. Cool-skinned people let top and heart notes speak longer.
- **Skin oiliness.** Oilier skin holds fragrance significantly longer. Drier skin needs richer compositions or more frequent reapplication.

If you know the recipient's skin tendency, you can adjust: lighter compositions for warm/oily skin, richer compositions for cool/dry skin.

## The presentation matters as much as the bottle

In the Gulf especially, a perfume given without ceremony is half a gift. The packaging, the card, the moment of presentation — all of these complete the message. A €200 bottle handed over in a plastic bag reads as a €50 gesture. A €100 bottle presented in a structured box with a handwritten note reads as the gift you intended.

This is why every [Lecmo](/) order ships gift-ready by default. We do not assume you have wrapping at home. The gift is the bottle plus the moment, and we build both.

## When in doubt

Choose a smaller bottle of something exceptional rather than a larger bottle of something safe. Quality is remembered. Volume is not.

And if you are still uncertain, write to us. We will help you choose. That is what a small house is for.`,
    },
    ar: {
      title: "كيف تختار عطراً هديّةً لمن تحبّ",
      excerpt:
        "اختيار عطرٍ لشخصٍ آخر هو الهديّة الأشدّ شخصيّةً التي يمكن أن تُقدِّمها. دليلٌ عمليّ لإصابة الاختيار.",
      body: `العطر الهديّة الأشدّ شخصيّةً في العالم. أنت لا تُقدِّم شيئاً فحسب؛ أنت تختار الهواء حول شخصٍ تحبّه. إليك كيف تفعلها بإحسان.

## ابدأ بما يرتديه أصلاً

أكثر معلومةٍ نافعة يمكنك جمعها هي العطر الذي يرتديه المُستلِم الآن. إن استطعت معرفة اسم عطرٍ واحدٍ يحبّه ويتناوله، فلديك خريطتك.

تخبرك تلك القارورة الواحدة:

- **عائلة نوتاته.** زهريّة، خشبيّة، شرقيّة، منعشة، حلوة. نادراً ما يقفز الناس بين العائلات.
- **تفضيله للتركيز.** مرتدو eau de toilette يميلون لكراهية مستخلصات parfum الكثيفة. مرتدو parfum يجدون EdTs رفيعةً.
- **شخصيّته.** ناعمٌ وأنيق؟ دافئٌ وجريء؟ هادئٌ وحميم؟

ثمّ اختر عطراً من العائلة ذاتها لكن ببُعدٍ جديد — قاعدةٌ أعمق قليلاً، زهرةٌ مختلفة قليلاً، توابلُ أكثر تعقيداً قليلاً. الحيّ ذاته، شارعٌ جديد.

## حين لا تعرف ما يرتديه

إن لم تكن لديك معلومات، التزم بالتعقيد الآمن. تجنّب طرفين:

- **مملٌّ زيادة.** العطور «المنعشة» العامّة منسيّة، وهديّةٌ منسيّة أسوأ من لا هديّة.
- **مُستقطِبٌ زيادة.** العود القويّ، الجلد الثقيل، أو النوتات الحيوانيّة شخصيّة. جميلةٌ للشخص المناسب، صادمةٌ للخطأ.

استهدف المنتصف: مؤلّفةٌ مبنيّةٌ جيّداً بدفءٍ ووضوح. عودٌ-وردٌ معاصر، عنبرٌ ناعم، مسكٌ أنيق. هذه تنجح مع معظم الناس لأنّها جميلةٌ كونيّاً، لا لأنّها مملّة.

## طابق المناسبة

يتغيّر العطر الصحيح بحسب السبب:

- **رمضان أو العيد.** اقترب من الكلاسيكيّ: عود، ورد، عنبر، مسك. النوتات التي تنتمي للموسم.
- **عُرس.** اقترب من الأناقة والزهور: ياسمين، زهور بيضاء، أخشابٌ ناعمة. تجنّب الحلويّات الثقيلة.
- **عيد ميلاد لمن تعرفه جيّداً.** اقترب من الشخصيّ: اختر عطراً يقول إنّك كنت تستمع.
- **هديّة عملٍ أو رسميّة.** اقترب من المُنظَّم والمُكرَّر: قارورةٌ وعلبةٌ تُشير إلى الاستثمار دون ألفة. تجنّب أيّ شيءٍ حميميٍّ صريح.

## طابق الجسد

يتفاعل العطر مختلفاً مع كيمياء البشرة. عاملان يهمّان أكثر:

- **دفء البشرة.** أصحاب البشرة الدافئة يضخّمون نوتات القاعدة (عود، عنبر، مسك) ويُسقِطونها أقوى. أصحاب البشرة الباردة يتركون النوتات العليا والقلب يتحدّثان أطول.
- **زيتيّة البشرة.** البشرة الأكثر زيتيّة تحمل العطر أطول بشكلٍ ملحوظ. البشرة الأكثر جفافاً تحتاج مؤلّفاتٍ أغنى أو إعادة تطبيقٍ أكثر تكراراً.

إن عرفت ميل بشرة المُستلِم، يمكنك التعديل: مؤلّفاتٌ أخفّ للبشرة الدافئة/الزيتيّة، مؤلّفاتٌ أغنى للبشرة الباردة/الجافّة.

## التقديم يهمّ بقدر القارورة

في الخليج خاصّةً، عطرٌ يُقدَّم بلا مراسم نصف هديّة. التغليف، البطاقة، لحظة التقديم — كلّ هذه تُكمِل الرسالة. قارورةٌ بـ٧٠٠ ريالٍ تُسلَّم في كيس بلاستيك تُقرَأ كإيماءةٍ بـ١٧٠. قارورةٌ بـ٤٠٠ ريالٍ تُقدَّم في علبةٍ منظَّمة مع بطاقةٍ مكتوبة بخطّ اليد تُقرَأ كالهديّة التي قصدتها.

لهذا كلّ طلبٍ من [Lecmo](/) يُشحَن جاهزاً للإهداء افتراضيّاً. لا نفترض أنّ لديك تغليفاً في البيت. الهديّة هي القارورة زائد اللحظة، ونحن نبني الاثنين.

## حين تشكّ

اختر قارورةً أصغر من شيءٍ استثنائيّ بدل قارورةٍ أكبر من شيءٍ آمن. الجودة تُذكَر. الحجم لا.

وإن كنت لا تزال غير متأكّد، اكتب لنا. سنساعدك في الاختيار. لذلك تكون الدار الصغيرة موجودة.`,
    },
    sources: [],
  },
];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

export function getPostBySlug(slug: string): JournalPost | undefined {
  return JOURNAL_POSTS.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug: string, limit = 2): JournalPost[] {
  const current = getPostBySlug(slug);
  if (!current) return [];
  return JOURNAL_POSTS
    .filter((p) => p.slug !== slug && p.category === current.category)
    .slice(0, limit);
}

export function getCategoryLabel(category: JournalCategory, locale: "en" | "ar"): string {
  const map = {
    ritual:  { en: "Ritual",   ar: "طقوس"   },
    house:   { en: "House",    ar: "الدار"  },
    craft:   { en: "Craft",    ar: "حِرَفة" },
    gifting: { en: "Gifting",  ar: "إهداء" },
  } as const;
  return map[category][locale];
}
